import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { FC, useEffect, useState, useRef, Fragment } from 'react';
import debounce from 'lodash.debounce';
import { TableCell, TableRow } from '@mui/material';
import {
  ConfirmationModalContent,
  DateAndTime,
  Dropdown,
  InputField,
  LoadingSpinner,
  ModalPopup,
  Table,
  Tooltip
} from 'components';
import { HeadCell } from 'components/table/table';
import { ClickAwayListener } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getMedicalVisits,
  deleteMedicalVisits,
  putMedicalVisits,
  MedicalVisitsItem
} from 'store/medicalVisits/medicalVisitsActions';
import { resetMedicalVisitsList } from 'store/medicalVisits/medicalVisitsSlice';

import style from './medicalVisits.module.scss';

const headCells: HeadCell[] = [
  {
    id: 'dateTime',
    label: 'Date and Time',
    sort: true,
    width: '15%'
  },
  {
    id: 'type',
    label: 'Type',
    sort: true,
    width: '14%'
  },
  {
    id: 'location',
    label: 'Location',
    sort: true,
    width: '14%'
  },
  {
    id: 'provider',
    label: 'Provider',
    sort: true,
    width: '14%'
  },
  {
    id: 'reason',
    label: 'Reason',
    sort: true,
    width: '16%'
  },
  {
    id: 'status',
    label: 'Status',
    sort: true,
    width: '13%'
  },
  {
    id: 'source',
    label: 'Source',
    sort: true,
    width: '14%'
  }
];

type FormikInputProps = {
  formik: FormikProps<any>;
  name: string;
  disabled?: boolean;
};

const FormikInput: FC<FormikInputProps> = ({ formik, name, disabled }) => {
  return (
    <TableCell>
      <InputField
        classnamesProps={style.inputField}
        id={name}
        name={name}
        type="text"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values[name] || ''}
        disabled={disabled}
      />
    </TableCell>
  );
};

const TableCellValue: FC<{ value?: string | null; maxWidth?: string }> = ({
  value,
  maxWidth = '120px'
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const { scrollWidth, offsetWidth } = textRef.current;
      setIsTextTruncated(scrollWidth > offsetWidth);
    }
  }, [value]);

  return (
    <TableCell sx={{ maxWidth }}>
      <div ref={textRef}>
        {isTextTruncated ? (
          <Tooltip
            toolTipText={isTextTruncated ? value : ''}
            disableInteractive
            blackTooltip
            placement="bottom-start"
            enterDelay={300}
          >
            <span className={clsx(style.valueWrap, style.truncatedText)}>{value}</span>
          </Tooltip>
        ) : (
          <span className={style.valueWrap}>{value}</span>
        )}
      </div>
    </TableCell>
  );
};

interface Props {
  isDisabled: boolean;
}

const MedicalVisitsTable: FC<Props> = ({ isDisabled }) => {
  const { clientId } = useParams();
  const dispatch = useAppDispatch();
  const { data: clientData } = useAppSelector((state) => state.clients.client);
  const canEditMedicalVisits = clientData?.medicalVisitsEditingAllowed;
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isAutosaving, setIsAutosaving] = useState<boolean | null>(null);
  const [typesOpen, setTypesOpen] = useState(false);
  const [statusesOpen, setStatusesOpen] = useState(false);
  const [entered, setEntered] = useState<boolean>(false);
  const [selectedVisit, setSelectedVisit] = useState<MedicalVisitsItem | null>(null);
  const { medicalVisitsList, medicalVisitsListLoading, medicalVisitsTypes, medicalVisitsStatuses } =
    useAppSelector((state) => state.medicalVisits);
  const { content, page, size, filters } = medicalVisitsList;
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (clientId) {
      dispatch(
        getMedicalVisits({
          clientId,
          page,
          size,
          sorts: null,
          filters: null
        })
      );
    }

    return () => {
      dispatch(resetMedicalVisitsList());
    };
  }, [clientId]);

  const handleTypesDropdownOpen = () => setTypesOpen(true);
  const handleTypesDropdownClose = () => setTypesOpen(false);
  const handleStatusesDropdownOpen = () => setStatusesOpen(true);
  const handleStatusesDropdownClose = () => setStatusesOpen(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...selectedVisit,
      typeId: selectedVisit?.typeId?.toString() ?? '',
      statusId: selectedVisit?.statusId?.toString() ?? '',
      time: selectedVisit?.timeOfEncounter
        ? dayjs(selectedVisit?.timeOfEncounter, 'hh:mm aa').format('hh:mm aa')
        : null
    },
    validationSchema: Yup.object().shape({
      dateOfEncounter: Yup.string().nullable(),
      timeOfEncounter: Yup.string().nullable(),
      time: Yup.string().nullable(),
      typeId: Yup.number().nullable(),
      location: Yup.string().nullable(),
      provider: Yup.string().nullable(),
      reason: Yup.string().nullable(),
      statusId: Yup.number().nullable()
    }),
    onSubmit: (values) => {
      if (clientId && selectedVisit?.id) {
        setIsAutosaving(true);
        const data: MedicalVisitsItem = {
          ...values,
          typeId: values.typeId ? parseInt(values.typeId, 10) : undefined,
          statusId: values.statusId ? parseInt(values.statusId, 10) : undefined,
          dateOfEncounter:
            values.dateOfEncounter && values.dateOfEncounter !== 'Invalid Date'
              ? values.dateOfEncounter
              : null,
          timeOfEncounter:
            values.time && values.time !== 'Invalid Date'
              ? dayjs(values.time, 'HH:mm:ss').format('hh:mm A')
              : selectedVisit?.timeOfEncounter
        };
        dispatch(putMedicalVisits({ clientId, visitId: selectedVisit.id, data })).then(
          (res: any) => {
            if (!res.error) {
              setIsAutosaving(false);
              if (entered) {
                setEditMode(false);
                setSelectedVisit(null);
                handleTypesDropdownClose();
                handleStatusesDropdownClose();
                setEntered(false);
              }
            }
          }
        );
      }
    }
  });

  const onCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedVisit(null);
    setEntered(false);
  };

  const handleDelete = async () => {
    if (clientId && selectedVisit?.id) {
      const res: any = await dispatch(deleteMedicalVisits({ clientId, visitId: selectedVisit.id }));
      if (!res.error) {
        onCancelDelete();
      }
    }
  };

  const getTableData = (data: any) => {
    if (clientId) {
      dispatch(
        getMedicalVisits({
          clientId,
          page,
          size,
          filters,
          ...data
        })
      );
    }
  };

  const debouncedAutosave = debounce(async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      await formik.submitForm();
    }
  }, 400);

  useEffect(() => {
    if (formik.dirty) {
      debouncedAutosave();
    }
    return () => {
      debouncedAutosave.cancel();
    };
  }, [formik.values]);

  const handleFieldChange = (field: string, value: any) => {
    formik.setFieldTouched(field);
    formik.setFieldValue(field, value);
  };

  return (
    <>
      <Table
        ref={tableRef}
        tableWrapClassName={clsx(style.tableWrap, isDisabled && style.tableDisabled)}
        totalNumber={0}
        page={0}
        size={size}
        isLoading={medicalVisitsListLoading}
        headCells={headCells}
        getTableData={getTableData}
        showEmptyHeader={true}
        columns={content.map((row) => (
          <Fragment key={row.id}>
            <ClickAwayListener
              onClickAway={(event) => {
                if (editMode && selectedVisit?.id === row.id) {
                  const target = event.target as Element;
                  if (target.closest('[data-test-id="medical-visits-form-table-warning"]')) {
                    return;
                  }
                  if (
                    !target.closest(`.${style.editMode}`) ||
                    !target.closest(`.${style.inputField}`) ||
                    !target.closest(`.${style.dropdownBox}`)
                  ) {
                    setEditMode(false);
                    setSelectedVisit(null);
                    handleTypesDropdownClose();
                    handleStatusesDropdownClose();
                  }
                }
              }}
            >
              <TableRow
                data-test-id="medical-visits-form-table"
                id={`row-${row.id}`}
                onDoubleClick={() => {
                  if (isDisabled || !canEditMedicalVisits) return;
                  setEditMode(true);
                  setSelectedVisit(row);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') {
                    setEntered(false);
                  }
                  if (e.key === 'Enter') {
                    setEntered(true);
                    formik.handleSubmit();
                  }
                }}
              >
                {editMode && selectedVisit?.id === row.id ? (
                  <>
                    <TableCell sx={{ maxWidth: '120px' }}>
                      <DateAndTime
                        dateValue={formik.values.dateOfEncounter || null}
                        datePickerStyles={style.inputField}
                        onChangeDate={(date: string) => {
                          formik.setFieldValue('dateOfEncounter', date).then(() => {
                            formik.setFieldTouched('dateOfEncounter', true);
                          });
                        }}
                        onBlurDate={() => formik.setFieldTouched('dateOfEncounter', true)}
                        timeValue={
                          formik.values.time && formik.values.time !== 'Invalid Date'
                            ? formik.values.time
                            : formik.values.timeOfEncounter
                            ? dayjs(formik.values.timeOfEncounter, 'hh:mm A').format('hh:mm:ss')
                            : null
                        }
                        timePickerStyles={style.inputField}
                        onChangeTime={(time: string) => {
                          if (time === 'Invalid Date') {
                            formik.setFieldValue('time', null).then(() => {
                              formik.setFieldTouched('time', true);
                            });
                          } else {
                            formik.setFieldValue('time', time).then(() => {
                              formik.setFieldTouched('time', true);
                            });
                          }
                        }}
                        onBlurTime={() => formik.setFieldTouched('time')}
                      />
                    </TableCell>
                    <TableCell>
                      <div className={style.dropdownBox}>
                        <div onClick={handleTypesDropdownOpen} className={style.dropdownOverBox} />
                        <Dropdown
                          className={style.dropdown}
                          onChange={(event) => {
                            handleFieldChange('typeId', event.target.value);
                            handleTypesDropdownClose();
                          }}
                          getOpen={(e) => {
                            if (!e && typesOpen) {
                              handleTypesDropdownClose();
                            }
                          }}
                          onBlur={formik.handleBlur}
                          open={typesOpen}
                          data={medicalVisitsTypes}
                          label="Select"
                          name="typeId"
                          id="typeId"
                          value={formik.values.typeId?.toString() ?? ''}
                          fitWidthMore={50}
                          fitWidth
                        />
                      </div>
                    </TableCell>
                    <FormikInput formik={formik} name="location" />
                    <FormikInput formik={formik} name="provider" />
                    <FormikInput formik={formik} name="reason" />
                    <TableCell>
                      <div className={style.dropdownBox}>
                        <div
                          onClick={handleStatusesDropdownOpen}
                          className={style.dropdownOverBox}
                        />
                        <Dropdown
                          className={style.dropdown}
                          onChange={(event) => {
                            handleFieldChange('statusId', event.target.value);
                            handleStatusesDropdownClose();
                          }}
                          getOpen={(e) => {
                            if (!e && statusesOpen) {
                              handleStatusesDropdownClose();
                            }
                          }}
                          onBlur={formik.handleBlur}
                          open={statusesOpen}
                          data={medicalVisitsStatuses}
                          label="Select"
                          name="statusId"
                          id="statusId"
                          value={formik.values.statusId?.toString() ?? ''}
                        />
                      </div>
                    </TableCell>
                    <FormikInput formik={formik} name="source" disabled />
                  </>
                ) : (
                  <>
                    <TableCellValue
                      value={`${
                        row.dateOfEncounter ? dayjs(row.dateOfEncounter).format('MM/DD/YY') : ''
                      } ${row.timeOfEncounter ?? ''}`}
                    />
                    <TableCellValue value={row.type} />
                    <TableCellValue value={row.location} />
                    <TableCellValue value={row.provider} />
                    <TableCellValue value={row.reason} maxWidth="140px" />
                    <TableCellValue value={row.status} />
                    <TableCell>
                      <div className={style.deleteWrap}>
                        <span>{row.source}</span>
                        <span
                          data-disabled={!canEditMedicalVisits}
                          onClick={() => {
                            setDeleteModalOpen(true);
                            setSelectedVisit(row);
                          }}
                          className={clsx('material-icons', style.icon)}
                        >
                          delete
                        </span>
                      </div>
                    </TableCell>
                  </>
                )}
              </TableRow>
            </ClickAwayListener>
          </Fragment>
        ))}
      />
      {editMode &&
        Object.keys(formik.errors).length === 0 &&
        Object.keys(formik.touched).length !== 0 &&
        isAutosaving !== null && (
          <div className={style.autosavedBox}>
            {isAutosaving ? (
              <LoadingSpinner size={20} />
            ) : (
              <>
                <span className="material-icons">check</span>Autosaved a few seconds ago
              </>
            )}
          </div>
        )}
      {Object.keys(formik.errors).length !== 0 &&
        Object.keys(formik.touched).length !== 0 &&
        editMode && (
          <div className={clsx(style.autosavedBox, style.autosavedError)}>
            <span className="material-icons-round">warning_amber</span>Unable to autosave. There are
            invalid values.
          </div>
        )}

      {!medicalVisitsListLoading && content.length === 0 && (
        <div className={style.noData}>
          <p>Nothing to see here!</p>
        </div>
      )}
      <ModalPopup
        open={deleteModalOpen}
        customClassInsideContainer={style.modalDelete}
        handleClose={onCancelDelete}
      >
        <ConfirmationModalContent
          confirmButtonText="Delete"
          rejectButtonText="Cancel"
          title="Are you sure you want to delete this encounter?"
          text=" "
          handleConfirm={handleDelete}
          handleReject={onCancelDelete}
        />
      </ModalPopup>
    </>
  );
};

export default MedicalVisitsTable;
